import React, { useState, useEffect } from "react";
import { string, array, func } from "prop-types";
import { ResponsiveImage } from "@quintype/components";
import "./agenda-section.m.css";
import PeopleIcon from "../../../../assets/icons/peopleicon";
import Download from "../../../../assets/icons/download";
import useIsMobile from "../../../hooks/useIsMobile";
import MinusIcon from "../../atoms/MinusIcon";
import PlusIcon from "../../atoms/PlusIcon";

export const AgendaSection = ({
  title = "",
  arrivalDate,
  arrivalText,
  departureDate,
  departureText,
  days = [],
  downloadLink,
  partnerDownloadLink,
  agendaDescription,
  isArrivalCard,
  isDepartureCard
}) => {
  // Use props data
  const agendaDays = days || [];
  const isMobile = useIsMobile();

  const [expandedDay, setExpandedDay] = useState(agendaDays.length > 0 ? 0 : null);
  const [expandedSessions, setExpandedSessions] = useState({});

  // Set first day and first session as expanded when days data becomes available
  useEffect(() => {
    if (agendaDays.length > 0 && agendaDays[0]?.sessions?.length > 0) {
      // Always set first session of first day as expanded on data load
      setExpandedSessions({ '0-0': true });
    }
  }, [agendaDays]);
  const arrival = {
    date: arrivalDate || "",
    text: arrivalText || "",
  };
  const departure = {
    date: departureDate || "",
    text: departureText || "",
  };
  const agendaDownloadLink = downloadLink || "";
  const agendaPartnerDownloadLink = partnerDownloadLink || "";

  const toggleDay = (index) => {
    const willExpand = expandedDay !== index;
    setExpandedDay(expandedDay === index ? null : index);

    // When expanding a day, automatically open its first session
    if (willExpand && agendaDays[index]?.sessions?.length > 0) {
      const firstSessionKey = `${index}-0`;
      setExpandedSessions({ [firstSessionKey]: true });
    }
  };

  const toggleSession = (dayIndex, sessionIndex) => {
    const key = `${dayIndex}-${sessionIndex}`;
    setExpandedSessions(prev => {
      const isCurrentlyExpanded = prev[key];

      // If clicking on already expanded session, close it
      if (isCurrentlyExpanded) {
        return {};
      }

      // Otherwise, close all others and open this one (accordion behavior)
      return { [key]: true };
    });
  };

  return (
    <div styleName="agenda-section" data-section="agenda">
      {/* Header */}
      <div styleName="agenda-header">
        <h2 styleName="agenda-title">{title}</h2>
        <div styleName="agenda-tabs">
          <div styleName="agenda-tab active">
            Participants
          </div>
          {/* <button styleName="agenda-tab" onClick={() => {
            const sponsorsSection = document.querySelector('[data-section="sponsors"]') || document.querySelector('[class*="sponsors-section"]');
            if (sponsorsSection) {
              sponsorsSection.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }
          }}>
            <PeopleIcon />
            Partners
          </button> */}
        </div>
      </div>

      {/* Agenda Content */}
      <div styleName="agenda-content">
        {/* Arrival Row */}
        {isArrivalCard && <div styleName="agenda-row arrival-row">
          <span styleName="day-label-small">Day 0</span>
          <span styleName="date-text" dangerouslySetInnerHTML={{ __html: arrival.date }} />
          <span styleName="event-text" dangerouslySetInnerHTML={{ __html: arrival.text }} />
        </div>}

        {/* Day Rows */}
        {Boolean(agendaDays?.length) && agendaDays.map((day, index) => (
          <div key={index} styleName="day-row-wrapper">
            <div
              styleName="agenda-row day-row"
              onClick={() => toggleDay(index)}
            >
              <span styleName="day-badge" dangerouslySetInnerHTML={{ __html: day.dayLabel }} />
              <div styleName="detail-wrapper">
                {!isMobile ?
                  <>
                    <span styleName="day-date" dangerouslySetInnerHTML={{ __html: day.date }} />
                    <span styleName="day-title" dangerouslySetInnerHTML={{ __html: day.title }} />
                    <button styleName="expand-btn" aria-label="Expand">
                      {expandedDay === index ? <MinusIcon color="#242424" /> : <PlusIcon color="#242424" />}
                    </button>
                  </> :
                  <>
                    <div styleName="first-line">
                      <span styleName="day-date" dangerouslySetInnerHTML={{ __html: day.date }} />
                      <button styleName="expand-btn" aria-label="Expand">
                        {expandedDay === index ? <MinusIcon color="#242424" /> : <PlusIcon color="#242424" />}
                      </button>
                    </div>
                    <div styleName="second-line">
                      <span styleName="day-title" dangerouslySetInnerHTML={{ __html: day.title }} />
                    </div>
                  </>}
              </div>
            </div>

            {/* Expanded Content */}
            {expandedDay === index && (
              <div styleName="day-expanded">
                {day.sessions && day.sessions.length > 0 ? (
                  day.sessions.map((session, sIndex) => {
                    const sessionKey = `${index}-${sIndex}`;
                    const isSessionExpanded = expandedSessions[sessionKey];

                    return (
                      <div
                        key={sIndex}
                        styleName={`session-item ${session.highlightColor === "green" ? "highlight-green" : ""}`}
                      >
                        {/* Row 1: Session Time and Title */}
                        <div styleName="session-time-title-row">
                          <div styleName="session-time-col">
                            <span styleName="session-time" dangerouslySetInnerHTML={{ __html: session.time }} />
                          </div>
                          <div styleName="session-title-col">
                            <div
                              styleName="session-header"
                              onClick={() => toggleSession(index, sIndex)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  toggleSession(index, sIndex);
                                }
                              }}
                            >
                              <span styleName="session-title" dangerouslySetInnerHTML={{ __html: session.title }} />
                              {(session?.description || session.location) && <button styleName="session-expand-btn" aria-label={isSessionExpanded ? "Collapse" : "Expand"}>
                                {isSessionExpanded ? <MinusIcon color="#242424" /> : <PlusIcon color="#242424" />}
                              </button>}
                            </div>
                          </div>
                        </div>

                        {/* Row 2: Session Location and Description (if expanded) */}
                        {isSessionExpanded && (session?.description || session.location) && (
                          <div styleName="session-location-description-row">
                            {(
                              <div styleName="session-location-col">
                                <span styleName="session-location" dangerouslySetInnerHTML={{ __html: session.location }} />
                              </div>
                            )}
                            <div styleName="session-description-col">
                              {session?.imgElement && (
                                <div styleName="session-image-item">
                                  {/* Check if imageUrl is an S3 key or a regular URL */}
                                  {(() => {
                                    const isS3Key = session.imgElement.imageUrl && !session.imgElement.imageUrl.startsWith('http');
                                    return isS3Key ? (
                                      <ResponsiveImage
                                        slug={session.imgElement.imageUrl}
                                        metadata={session.imgElement.imageMetadata || {}}
                                        aspectRatio={[16, 9]}
                                        defaultWidth={200}
                                        widths={[200, 400]}
                                        imgParams={{ auto: ["format", "compress"] }}
                                        alt={session.imgElement.alt || 'Session image'}
                                        styleName="session-image"
                                      />
                                    ) : (
                                      <img
                                        src={session.imgElement.imageUrl}
                                        alt={session.imgElement.alt || 'Session image'}
                                        styleName="session-image"
                                      />
                                    );
                                  })()}
                                </div>
                              )}
                              {session?.description && (
                                <p styleName="session-description" dangerouslySetInnerHTML={{ __html: session.description }} />
                              )}
                              {session.speakers && session.speakers.length > 0 && (
                                <div styleName="session-speakers">
                                  {session.speakers.map((speaker, spIndex) => {
                                    // Check if imageUrl is an S3 key or a regular URL
                                    const isS3Key = speaker.imageUrl && !speaker.imageUrl.startsWith('http');

                                    // Determine image style class based on session's imagetype
                                    const imageStyleClass = session.imagetype === "normal"
                                      ? "speaker-image speaker-image-normal"
                                      : "speaker-image speaker-image-rounded";

                                    return (
                                      <div key={spIndex} styleName="speaker-item">
                                        {isS3Key ? (
                                          <ResponsiveImage
                                            slug={speaker.imageUrl}
                                            metadata={speaker.imageMetadata || {}}
                                            aspectRatio={[1, 1]}
                                            defaultWidth={40}
                                            widths={[40, 80]}
                                            imgParams={{ auto: ["format", "compress"] }}
                                            alt={speaker.name}
                                            styleName={imageStyleClass}
                                          />
                                        ) : (
                                          <img
                                            src={speaker.imageUrl}
                                            alt={speaker.name}
                                            styleName={imageStyleClass}
                                          />
                                        )}
                                        <div styleName="speaker-info">
                                          {speaker.caption && (
                                            <span styleName="speaker-caption" dangerouslySetInnerHTML={{ __html: speaker.caption }} />
                                          )}
                                          {speaker.attribution && (
                                            <span styleName="speaker-attribution" dangerouslySetInnerHTML={{ __html: speaker.attribution }} />
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              {session.note && (
                                <p styleName="session-note">
                                  <span dangerouslySetInnerHTML={{ __html: session.note }} />
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div styleName="session-item">
                    <div styleName="session-content-col">
                      <p styleName="session-description">Sessions to be announced</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Departure Row */}
        {((isDepartureCard && (departure.date || departure.text)) || agendaDownloadLink || agendaPartnerDownloadLink) && (
          <div styleName={!isDepartureCard ? "agenda-row departure-row download-only" : "agenda-row departure-row"}>
            {isDepartureCard && (
              <>
                <span styleName="day-label-small">Day {agendaDays?.length + 1}</span>
                <span styleName="date-text" dangerouslySetInnerHTML={{ __html: departure.date }} />
                <span styleName="event-text" dangerouslySetInnerHTML={{ __html: departure.text }} />
              </>
            )}
            {(agendaDownloadLink || agendaPartnerDownloadLink) && <div styleName="download-links-container">
            {agendaDownloadLink && <a
              href={agendaDownloadLink}
              styleName="download-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              Download Participants Agenda
              <Download />
            </a>}
            {agendaPartnerDownloadLink && <a
              href={agendaPartnerDownloadLink}
              styleName="download-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              Download Partners Agenda
              <Download />
            </a>}
            </div>}
          </div>
        )}
      </div>

      {/* Footer */}
      {agendaDescription && <div styleName="agenda-footer footer-text">
        <span styleName="footer-text" dangerouslySetInnerHTML={{ __html: agendaDescription }} />
      </div>}
    </div>
  );
};

AgendaSection.propTypes = {
  title: string,
  arrivalDate: string,
  arrivalText: string,
  departureDate: string,
  departureText: string,
  days: array,
  downloadLink: string,
  agendaDescription: string,
};

